// The On-duty segment's fit ladder.
//
// The command strip has no spare width. At 1280 logical with the sidebar
// permanently collapsed the rail is 1164 wide (2px of it the segments' own
// borders). In residence hugs at 85; the attention segment is FIXED at 357
// (Sir, 2026-09-02; 396 before, and briefly a box that hugged its content,
// which moved the roster on every date step) — so On duty has 720:
// 357 + 720 + 85 = 1162, less on a week with a two-digit count, when the
// attention box grows for that week alone. The attention segment was fixed at 211 before this
// feature, and 149 back when it hugged its content and this arithmetic was
// first written (PR #166, claude).
//
// THAT WIDENING COST THE ROSTER A RUNG, and the claim it replaces should be read
// as the warning it turned out to be. This comment once said a full
// eight-helper roster rendered "in full, unshortened, on the `bare` rung" at
// 1280. At 396 it did not: measured against the same fixed eight-helper
// fixture, 1280 landed on **`clipped`** — every face and every name present,
// the names shortened. At 357 the roster has 39px back and 1280 lands on
// **`bare`** again — every name whole, no chip boxes — which
// `daily-command-strip.spec.ts` pins; 1440 lands on `full`.
//
// That is the ladder doing precisely what Freddi asked of it (2026-08-17): the
// roster gives up a rung, and shortens a NAME before it gives one up, rather
// than the bar spending its vocabulary. It is not a regression. It is, however,
// a real cost of the wider segment, and it was only found because a reviewer
// noticed this paragraph had stopped being true.
//
// The roster is the only
// one carrying anything it can give up — so when it will not fit, it steps DOWN
// a rung rather than overrunning its neighbour or clipping a name in half.
//
// What it sheds, in order: the chip box around each helper, then the LENGTH of
// the name, then the name itself. What it NEVER sheds is the face. "Every
// rostered helper shows their avatar" is an acceptance criterion, not a
// preference, and the avatar is deliberately absent from every decision below.
//
// The shortening rung is Freddi's (2026-08-17): "once we hit breaking point with
// screen sizes we can just use elipsis and then avatars without names as like a
// scaling down process". The original rule forbade ellipsis outright, which left
// exactly one step between a whole name and no name — so the moment the rail ran
// short, eight first names became eight anonymous faces. A shortened name is
// still a name; a face on its own is a puzzle.
//
// The measuring is DOM work and belongs to the component; this module is the
// part that can be reasoned about without a browser, which is where the
// invariant is worth pinning.

export type DutyMode = 'full' | 'bare' | 'clipped' | 'avatars';

/** Rungs, richest first. Every one of them draws the avatar. */
export const DUTY_LADDER: readonly DutyMode[] = ['full', 'bare', 'clipped', 'avatars'] as const;

/** Does this rung draw the helper's name beside their face? */
export const showsName = (mode: DutyMode): boolean => mode !== 'avatars';

/** Does this rung shorten the name to fit, rather than drop it? */
export const clipsName = (mode: DutyMode): boolean => mode === 'clipped';

/** Does this rung draw the bordered chip around the pair? */
export const showsChipBox = (mode: DutyMode): boolean => mode === 'full';

/**
 * The rung `steps` below `preferred`.
 *
 * Clamped at both ends. The floor matters because the component re-renders on
 * each step and an unclamped index would return `undefined` — rendering nothing
 * at all, which is a worse answer to "it does not fit" than rendering faces.
 * A negative step is treated as no step: the ladder only ever descends, and it
 * is reset to the preferred rung when the inputs change rather than climbed.
 */
export function rungAfter(preferred: DutyMode, steps: number): DutyMode {
  const from = DUTY_LADDER.indexOf(preferred);
  const start = from === -1 ? 0 : from;
  const index = Math.min(start + Math.max(0, steps), DUTY_LADDER.length - 1);
  return DUTY_LADDER[index]!;
}
